import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import logo from "../../images/logo.png";
import "./Voucher.css";
import {useAppContext} from "../../contextApi/AppContext";

function Voucher() {
  const [searchParams] = useSearchParams();
  const [student, setStudent] = useState(null);
  const [courses, setCourses] = useState([]);
  const [aboutCourse, setAboutCourse] = useState([]);
  const [feeHistory, setFeeHistory] = useState([]);
  const [allFees, setAllFees] = useState([]);
  const [institution, setInstitution] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPrinting, setIsPrinting] = useState(false);
  const voucherRef = useRef(null);
  const {classOptions} = useAppContext();

  const API_BASE = "https://api.theecportal.com/api";
  const studentIdFromParams = searchParams.get("studentId");
  const monthFromParams = searchParams.get("month");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // First check if student data exists in localStorage (from admin registration)
        const storedStudent = localStorage.getItem("voucherStudent");
        const storedStudentId =
          localStorage.getItem("voucherStudentId") || studentIdFromParams;
        const storedFeeHistory = localStorage.getItem("feeHistory");

        let studentData = null;

        if (storedStudent) {
          studentData = JSON.parse(storedStudent);
          setStudent(studentData);
          console.log("Loaded student data from localStorage", studentData);
        } else if (storedStudentId) {
          // If only student ID is available, fetch student data
          const studentRes = await axios.get(
            `${API_BASE}/students/getStudentById/${storedStudentId}`,
            {
              headers: getAuthHeaders(),
            },
          );
          if (studentRes.data?.success) {
            studentData = studentRes.data.student;
            setStudent(studentData);
          }
        } else {
          // Fallback: get current user's profile
          const profileRes = await axios.get(`${API_BASE}/students/myProfile`, {
            headers: getAuthHeaders(),
          });
          if (profileRes.data?.success) {
            studentData = profileRes.data.student;
            setStudent(studentData);
          }
        }
        console.log("Student data after fetching:", studentData);
        // Fetch fee history from stored data or API
        if (storedFeeHistory) {
          setFeeHistory(JSON.parse(storedFeeHistory));
          console.log(
            "Loaded fee history from localStorage",
            JSON.parse(storedFeeHistory),
          );
        }
        if (storedStudentId || studentData?._id) {
          console.log(
            "Fetching fee for month:",
            monthFromParams,
            "for student ID:",
            storedStudentId || studentData._id,
          );
          try {
            // Fetch ALL fees for the student
            let allFeesUrl = `${API_BASE}/students/getStudentFee/${storedStudentId || studentData._id}?feeFetchType=all`;
            const allFeesRes = await axios.get(allFeesUrl, {
              headers: getAuthHeaders(),
            });
            if (allFeesRes.data?.success && allFeesRes.data.fees) {
              setAllFees(allFeesRes.data.fees);
              console.log("Fetched all fees from API", allFeesRes.data.fees);
            }

            // Fetch current month fee for voucher
            let feeUrl = `${API_BASE}/students/getStudentFee/${storedStudentId || studentData._id}`;
            if (monthFromParams) {
              feeUrl += `?month=${monthFromParams}`;
            }

            const feeRes = await axios.get(feeUrl, {
              headers: getAuthHeaders(),
            });
            if (feeRes.data?.success && feeRes.data.fees) {
              setFeeHistory(feeRes.data.fees);
              console.log("Fetched fee for month from API", feeRes.data.fees);
            }
          } catch (error) {
            console.log("Fee history not available");
          }
        }

        // Fetch courses for the student
        if (studentData) {
          let coursesRes;
          if (storedStudentId) {
            // Fetch courses for specific student by student ID
            coursesRes = await axios.get(
              `${API_BASE}/registration/getStudentCourses/${storedStudentId}`,
              {
                headers: getAuthHeaders(),
              },
            );
          } else {
            // Fetch current user's courses
            coursesRes = await axios.get(`${API_BASE}/registration/myCourses`, {
              headers: getAuthHeaders(),
            });
          }

          if (coursesRes.data?.success) {
            setCourses(coursesRes.data.courses || []);
            setAboutCourse(coursesRes.data.aboutCourse || []);
          } else if (coursesRes.data?.courses) {
            setCourses(coursesRes.data.courses);
            setAboutCourse(coursesRes.data.aboutCourse || []);
          }
        }
      } catch (error) {
        console.error("Error fetching voucher data:", error);
        toast.error("Failed to load voucher data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [studentIdFromParams, monthFromParams]);

  // Auto-download PDF on mount
  useEffect(() => {
    if (!loading && student && aboutCourse.length > 0 && voucherRef.current) {
      const timer = setTimeout(() => {
        downloadPDF(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [loading, student, aboutCourse]);

  // Cleanup localStorage when component unmounts
  useEffect(() => {
    return () => {
      // Optionally clear localStorage when leaving the page
      // localStorage.removeItem("voucherStudent");
      // localStorage.removeItem("voucherStudentId");
    };
  }, []);

  // Handle print events
  useEffect(() => {
    const handleBeforePrint = () => {
      setIsPrinting(true);
    };

    const handleAfterPrint = () => {
      setIsPrinting(false);
    };

    window.addEventListener("beforeprint", handleBeforePrint);
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      window.removeEventListener("beforeprint", handleBeforePrint);
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, []);

  const calculateTotals = () => {
    // Use fee data if available (already has prorated calculation)
    if (feeHistory && feeHistory.length > 0) {
      const fee = feeHistory[0];
      return {
        totalOriginalFee: Number(fee.actualFee || 0),
        totalDiscountedFee: Number(fee.finalFee || 0),
        totalDiscount: Number(fee.discount || 0),
      };
    }

    // Fallback to aboutCourse calculation (for full month)
    let totalOriginalFee = 0;
    let totalDiscountedFee = 0;

    aboutCourse.forEach((item) => {
      const originalFee = Number(item?.courseActualPrice || 0);
      const discountedFee = Number(item?.courseDiscountedPrice || 0);

      totalOriginalFee += originalFee;
      totalDiscountedFee += discountedFee;
    });

    const totalDiscount = totalOriginalFee - totalDiscountedFee;

    return {
      totalOriginalFee,
      totalDiscountedFee,
      totalDiscount,
    };
  };

  const calculateFeeTotals = () => {
    let paidAmount = 0;
    let remainingAmount = 0;

    feeHistory.forEach((fee) => {
      paidAmount += Number(fee.amountPaid || 0);
      remainingAmount += Number(fee.remainingFee || 0);
    });

    return {
      paidAmount,
      remainingAmount,
    };
  };

  const calculateOutstandingFees = () => {
    // Get current month from feeHistory
    const currentMonthFee =
      feeHistory && feeHistory.length > 0 ? feeHistory[0]?.month : null;

    let outstandingAmount = 0;
    let outstandingFees = [];

    // Calculate unpaid/partial fees from previous months
    allFees.forEach((fee) => {
      // Skip current month
      if (fee.month === currentMonthFee) return;

      const status = fee.status?.toLowerCase();

      // Add fees that are unpaid or partial
      if (status === "unpaid" || status === "partial") {
        const remaining = Number(fee.remainingFee || 0);
        outstandingAmount += remaining;
        outstandingFees.push({
          month: fee.month,
          remaining: remaining,
          status: status,
        });
      }
    });

    return {
      outstandingAmount,
      outstandingFees,
    };
  };

  const calculateTotalPayable = () => {
    // Current month payable
    const currentFeeTotal = calculateTotals();
    const currentMonthPayable = currentFeeTotal.totalDiscountedFee;

    // Outstanding fees from previous months
    const outstanding = calculateOutstandingFees();

    // Total payable = current month + outstanding
    const totalPayable = currentMonthPayable + outstanding.outstandingAmount;

    return {
      currentMonthPayable,
      outstandingAmount: outstanding.outstandingAmount,
      outstandingFees: outstanding.outstandingFees,
      totalPayable,
    };
  };

  const calculateFeeStatus = () => {
    if (feeHistory.length === 0) return "Unpaid";
    const statuses = feeHistory.map((f) => f.status);
    if (statuses.every((s) => s === "paid")) return "Paid";
    if (statuses.some((s) => s === "paid" || s === "partial")) return "Partial";
    return "Unpaid";
  };

  const totals = calculateTotals();

  const downloadPDF = async (isAuto = false) => {
    try {
      // Get the first fee month for filename
      const firstFee =
        feeHistory && feeHistory.length > 0 ? feeHistory[0] : null;
      let month = "Voucher";

      if (firstFee?.month) {
        const monthMatch = firstFee.month.match(/^(\d{4})-(\d{2})$/);
        if (monthMatch) {
          const year = monthMatch[1];
          const monthNum = parseInt(monthMatch[2], 10);
          const date = new Date(year, monthNum - 1, 1);
          month = date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          });
        } else {
          month = firstFee.month;
        }
      }

      const studentName = student?.name || "Student";
      const filename = `${studentName}_Voucher_${month}`;

      // Set document title for PDF filename
      const originalTitle = document.title;
      document.title = filename;

      setTimeout(() => {
        window.print();
        document.title = originalTitle;
      }, 200);
    } catch (error) {
      console.error("Error generating PDF:", error);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-success" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const month = () => {
    const firstFee = feeHistory && feeHistory.length > 0 ? feeHistory[0] : null;
    if (firstFee?.month) {
      const monthMatch = firstFee.month.match(/^(\d{4})-(\d{2})$/);
      if (monthMatch) {
        const year = monthMatch[1];
        const monthNum = parseInt(monthMatch[2], 10);
        const date = new Date(year, monthNum - 1, 1);
        return date.toLocaleString("default", {
          month: "long",
          year: "numeric",
        });
      }
      return firstFee.month;
    }
  };

  return (
    <div className="voucher-page">
      <div
        className={`voucher-print-header ${isPrinting ? "is-printing" : ""}`}
      >
        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            flexDirection: "column",
            width: "fit-content",
          }}
        >
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => downloadPDF()}
            style={{ display: isPrinting ? "none" : "block" }}
          >
            <i className="fas fa-print me-2"></i>
            Print / Download PDF
          </button>
          {student?.fatherContact && (
            <a
              href={`https://wa.me/${student.fatherContact.replace(/\D/g, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn"
              style={{
                display: isPrinting ? "none" : "inline-block",
                backgroundColor: "#25D366",
                color: "white",
                border: "none",
                textDecoration: "none",
                padding: "0.375rem 0.75rem",
                borderRadius: "0.25rem",
                fontSize: "0.875rem",
                fontWeight: "500",
              }}
            >
              <i className="fab fa-whatsapp me-2"></i>
              WhatsApp Parent
            </a>
          )}
        </div>
      </div>
      <div className="voucher-container" ref={voucherRef}>
        {/* Watermark */}
        <div className="voucher-watermark">
          <img
            src={logo}
            alt="EC Academy Watermark"
            className="watermark-logo"
          />
        </div>

        {/* Header */}
        <div className="voucher-header">
          <div className="voucher-logo-section">
            <img src={logo} alt="EC Academy" className="voucher-logo" />
          </div>
          <div className="voucher-title-section">
            <h1 className="voucher-main-title">
              THE EDUCATION'S CRADLE INSTITUTE
            </h1>
            <h2 className="voucher-subtitle">Fee Voucher for {month()}</h2>
          </div>
        </div>

        <div className="voucher-divider"></div>

        {/* Payment Details Section - Moved to Top */}
        <section className="voucher-section payment-details-section">
          <h3 className="section-title">Payment Details</h3>
          <div className="payment-info">
            <div className="payment-item">
              <span className="payment-label">Account Title:</span>
              <span className="payment-value">MUHAMMAD ALI</span>
            </div>

            <div className="payment-item">
              <span className="payment-label">Account Number:</span>
              <span className="payment-value">03164752672</span>
            </div>
            <div className="payment-item">
              <span className="payment-label">Bank:</span>
              <span className="payment-value">EASYPAISA</span>
            </div>
          </div>
          <div className="payment-info">
            <div className="payment-item">
              <span className="payment-label">Account Title:</span>
              <span className="payment-value">MUHAMMAD ALI</span>
            </div>

            <div className="payment-item">
              <span className="payment-label">IBAN Number:</span>
              <span className="payment-value">PK17ALFH0481001010298766</span>
            </div>
            <div className="payment-item">
              <span className="payment-label">Bank:</span>
              <span className="payment-value">BANK ALFALAH</span>
            </div>
          </div>
        </section>

        {/* Courses and Fees Section */}
        <section className="voucher-section">
          <h3 className="section-title">Student Information</h3>
          <div className="student-info-grid">
            <div className="info-item">
              <label>Student Name</label>
              <div className="info-value">{student?.name || "N/A"}</div>
            </div>

            <div className="info-item">
              <label>Father Name</label>
              <div className="info-value">{student?.fatherName || "N/A"}</div>
            </div>

            <div className="info-item">
              <label>Father Contact</label>
              <div className="info-value">
                {student?.fatherContact || "N/A"}
              </div>
            </div>

            <div className="info-item">
              <label className="info-label">Roll Number</label>
              <div className="info-value">{student?.rollNumber || "N/A"}</div>
            </div>

            <div className="info-item">
              <label className="info-label">Class/Grade</label>
              <div className="info-value">
                {classOptions.find((c) => c.value === student?.classId)?.name ||
                  "N/A"}
              </div>
            </div>

            <div className="info-item">
              <label className="info-label">Due Date</label>
              <div className="info-value">
                {feeHistory && feeHistory.length > 0 && feeHistory[0]?.dueDate
                  ? new Date(feeHistory[0].dueDate).toLocaleDateString("en-GB")
                  : "N/A"}
              </div>
            </div>
          </div>
        </section>

        {/* Courses and Fees Section */}
        <section className="voucher-section">
          <h3 className="section-title">Registered Courses & Fees</h3>
          <table className="voucher-table">
            <thead>
              <tr>
                <th className="col-index">#</th>
                <th className="col-course">Course Name</th>
                <th className="col-original">Original Fee</th>
                <th className="col-discount">Discount</th>
                <th className="col-discounted">Discounted Fee</th>
              </tr>
            </thead>
            <tbody>
              {aboutCourse && aboutCourse.length > 0 ? (
                aboutCourse.map((item, idx) => {
                  const originalFee = Number(item?.courseActualPrice || 0);
                  const discountedFee = Number(
                    item?.courseDiscountedPrice || 0,
                  );
                  const discount = originalFee - discountedFee;
                  const courseTitle = item?.course?.title || "N/A";

                  return (
                    <tr key={item._id || idx}>
                      <td className="col-index">{idx + 1}</td>
                      <td className="col-course">{courseTitle}</td>
                      <td className="col-original">
                        PKR {originalFee.toLocaleString()}
                      </td>
                      <td className="col-discount">
                        PKR {discount.toLocaleString()}
                      </td>
                      <td className="col-discounted">
                        PKR {discountedFee.toLocaleString()}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No courses registered
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </section>

        {/* Proration Note Section */}
        {feeHistory && feeHistory.length > 0 && feeHistory[0]?.isProrated && (
          <section
            className="voucher-section"
            style={{
              backgroundColor: "#f0f4ff",
              borderLeft: "4px solid #3b82f6",
              padding: "1rem",
            }}
          >
            <p style={{ margin: "0", fontSize: "0.9rem", color: "#1e40af" }}>
              <strong>Note:</strong> This is a prorated fee for{" "}
              <strong>{feeHistory[0].proratedDays} days</strong> from{" "}
              <strong>
                {new Date(feeHistory[0].proratedFromDate).toLocaleDateString(
                  "en-GB",
                )}
              </strong>{" "}
              to{" "}
              <strong>
                {new Date(feeHistory[0].proratedToDate).toLocaleDateString(
                  "en-GB",
                )}
              </strong>{" "}
              (end of month). Fee calculated as: Monthly Fee ÷ Total Days in
              Month × {feeHistory[0].proratedDays} Days
            </p>
          </section>
        )}

        {/* Fee Status Section */}
        {feeHistory && feeHistory.length > 0 && (
          <section className="voucher-section fee-payment-section">
            <h3 className="section-title mt-2">Payment Status</h3>
            <div className="payment-status-grid">
              <div className="payment-status-item">
                <span className="payment-status-label">Status</span>
                <span
                  className={`payment-status-badge status-${calculateFeeStatus().toLowerCase()}`}
                >
                  {calculateFeeStatus()}
                </span>
              </div>
              <div className="payment-status-item">
                <span className="payment-status-label">Total Paid</span>
                <span className="payment-status-value">
                  PKR {calculateFeeTotals().paidAmount.toLocaleString()}
                </span>
              </div>
              <div className="payment-status-item">
                <span className="payment-status-label">Remaining</span>
                <span className="payment-status-value">
                  PKR {calculateFeeTotals().remainingAmount.toLocaleString()}
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Summary Section */}
        <section className="voucher-section">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 2fr",
              gap: "1.5rem",
            }}
          >
            {/* Left Column - Arrears Details (4 columns width) */}
            <div
              style={{
                padding: "1rem",
                backgroundColor: "#fff3cd",
                borderRadius: "0.5rem",
              }}
            >
              <h4
                style={{
                  fontSize: "0.95rem",
                  marginBottom: "1rem",
                  fontWeight: "600",
                }}
              >
                Arrears Details:
              </h4>
              {calculateTotalPayable().outstandingFees &&
              calculateTotalPayable().outstandingFees.length > 0 ? (
                <div>
                  {calculateTotalPayable().outstandingFees.map((fee, idx) => (
                    <div
                      key={idx}
                      style={{
                        fontSize: "0.85rem",
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "0.5rem",
                        paddingBottom: "0.5rem",
                        borderBottom: "1px solid rgba(0,0,0,0.1)",
                      }}
                    >
                      <span>{fee.month}</span>
                      <span style={{ fontWeight: "600" }}>
                        PKR {fee.remaining.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: "0.85rem", color: "#666", margin: "0" }}>
                  No arrears
                </p>
              )}
            </div>

            {/* Right Column - Fee Calculation Summary (8 columns width) */}
            <div
              style={{
                padding: "1rem",
                backgroundColor: "#e6f4ea",
                borderRadius: "0.5rem",
                borderLeft: "4px solid #198754",
              }}
            >
              <h4
                style={{
                  fontSize: "0.95rem",
                  marginBottom: "1rem",
                  fontWeight: "600",
                }}
              >
                Fee Summary:
              </h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.6rem",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "0.9rem",
                    paddingBottom: "0.5rem",
                    borderBottom: "1px solid rgba(0,0,0,0.1)",
                  }}
                >
                  <span>This Month Payable:</span>
                  <span style={{ fontWeight: "600" }}>
                    PKR{" "}
                    {calculateTotalPayable().currentMonthPayable.toLocaleString()}
                  </span>
                </div>

                {calculateTotalPayable().outstandingAmount > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.9rem",
                      paddingBottom: "0.5rem",
                      borderBottom: "1px solid rgba(0,0,0,0.1)",
                    }}
                  >
                    <span>Arrears (Previous Months):</span>
                    <span style={{ fontWeight: "600", color: "#d84315" }}>
                      PKR{" "}
                      {calculateTotalPayable().outstandingAmount.toLocaleString()}
                    </span>
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "1rem",
                    fontWeight: "700",
                    color: "#198754",
                    paddingTop: "0.5rem",
                  }}
                >
                  <span>Total Payable:</span>
                  <span>
                    PKR {calculateTotalPayable().totalPayable.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <div className="voucher-footer">
          <p>
            <strong>Note:</strong> Please keep this voucher safe for your
            records. Submit the proof of payment to the administration office
            within 7 days.
          </p>
          <div className="voucher-dates">
            <p className="voucher-date">
              Generated on: {new Date().toLocaleDateString("en-GB")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Voucher;
